import { Component, ViewChild, AfterViewInit, Input, Output, EventEmitter, ChangeDetectorRef, SimpleChanges } from '@angular/core';
import { AuthState, DashboardFilterData, NotificationsState } from '@portals/store';
import { IResponseChart, IParseChart } from '@portals/api';

import { finalize, tap } from 'rxjs';
import { BaseComponent } from '@portals/core';
import * as moment from 'moment';
import { environment } from '@portals/environments';
import { ChartData, ChartOptions, ChartConfiguration } from 'chart.js';
import { MatDialog } from '@angular/material/dialog';

import { format } from 'date-fns';
@Component({
	selector: 'portals-dashboard-responses',
	templateUrl: './responses.component.html',
	styleUrls: ['./responses.component.scss'],
})
export class ResponsesComponent extends BaseComponent implements AfterViewInit {
	@Input() projectID!: number;
	@Input() currentFilters!: any;
	@Input() timeframe = 'monthly';
	@Output() responsesCountEmit = new EventEmitter<any>();

	public lineChartData: ChartData<'line'> = {
		datasets: [],
		labels: [],
	};
	public lineChartOptions: ChartOptions<'line'> = {
		responsive: true,
		maintainAspectRatio: false,
		elements: {
			point: {
				radius: 0,
				hitRadius: 10,
			},
		},
		scales: {
			x: {
				grid: {
					display: true,
				},
				ticks: {
					maxRotation: 0,
					minRotation: 0,
					autoSkip: true,
					maxTicksLimit: 10,
				},
			},
			y: {
				grid: {
					display: true,
				},
			},
		},
		plugins: {
			legend: {
				display: true,
				position: 'top',
				labels: {
					boxWidth: 10,
					boxHeight: 10,
					pointStyle: 'circle',
					usePointStyle: true,
				},
			},
		},
	};
	public lineChartLegend = true;
	public lineChartPlugins = [];

	parsedData!: IParseChart;

	labels!: string[];

	// Number to change on tooltip changes
	allCount = 0;
	allProjectCount = 0;
	allTextCount = 0;
	projectTextCount = 0;
	allReportCount = 0;
	projectReportCount = 0;
	allPoliceReportCount = 0;
	projectPoliceReportCount = 0;
	allAudioCount = 0; // Add this property to fix the error
	projectAudioCount = 0; // Add this property to fix the error
	allVideoCount = 0; // Add this property to fix the error
	projectVideoCount = 0; // Add this property to fix the error
	date: Array<any> = [];
	datetime: Date = new Date();
	dateInfo = '';

	timeframes = ['daily', 'weekly', 'monthly', 'yearly'];
	formLoading = false;
	app = environment.app;
	admin = false;

	constructor(private notification: NotificationsState, private authState: AuthState, private cdr: ChangeDetectorRef) {
		super();
		this.admin = ['super_admin', 'aclu_admin'].includes(this.authState.data.role.type);
	}

	ngAfterViewInit() {
		this.getChart(this.timeframe, this.currentFilters);
	}
	ngOnChanges(changes: SimpleChanges) {
		if (changes['currentFilters']) {
			const previousValue = changes['currentFilters'].previousValue;
			const currentValue = changes['currentFilters'].currentValue;

			if (!this.isEqual(previousValue, currentValue)) {
				this.getChart(this.timeframe, currentValue);
				this.cdr.detectChanges();
			}
		}
	}
	private isEqual(obj1: any, obj2: any): boolean {
		return JSON.stringify(obj1) === JSON.stringify(obj2);
	}

	async getChart(timeframe: string, filters: any) {
		this.formLoading = true;
		this.subscribe(
			this.notification.getReponseChart(timeframe, this.currentFilters, this.projectID).pipe(
				tap((data: IResponseChart) => {
					this.chartData = data;
					this.date = [];
					this.series = [{}, {}, {}, {}];
					const allResponses = [];
					const allProjectResponses = [];
					const allAudios = [];
					const projectAudios = [];
					const allVideos = [];
					const projectVideos = [];
					const allTexts = [];
					const projectTexts = [];
					const allReports = [];
					const projectReports = [];
					const allPoliceReports = [];
					const projectPoliceReports = [];

					for (let i = 0; i < data.allResponses.length; i++) {
						const currentAllResponses = data.allResponses[i];
						const currentAllProjectResponses = data?.allProjectResponses ? data?.allProjectResponses[i] : null;
						const currentVideos = data?.allVideos ? data?.allVideos[i] : null;
						const currentProjectVideos = data?.projectVideos ? data?.projectVideos[i] : null;
						const currentTexts = data?.allTexts ? data?.allTexts[i] : null;
						const currentProjectTexts = data?.projectTexts ? data?.projectTexts[i] : null;
						const currentReports = data?.allReports ? data?.allReports[i] : null;
						const currentProjectReports = data?.projectReports ? data?.projectReports[i] : null;
						const currentPoliceReports = data?.allPoliceReports ? data?.allPoliceReports[i] : null;
						const currentProjectPoliceReports = data?.projectPoliceReports ? data?.projectPoliceReports[i] : null;
						const currentAudios = data?.allAudios ? data?.allAudios[i] : null;
						const currentProjectAudios = data?.projectAudios ? data?.projectAudios[i] : null;

						this.date.push(currentAudios?.date || new Date());

						allResponses.push(parseInt(currentAllResponses?.row_count));
						allProjectResponses.push(parseInt(<string>currentAllProjectResponses?.row_count));
						allAudios.push(parseInt(<string>currentAudios?.row_count));
						projectAudios.push(parseInt(<string>currentProjectAudios?.row_count));
						allVideos.push(parseInt(<string>currentVideos?.row_count));
						projectVideos.push(parseInt(<string>currentProjectVideos?.row_count));
						allTexts.push(parseInt(<string>currentTexts?.row_count));
						projectTexts.push(parseInt(<string>currentProjectTexts?.row_count));
						allReports.push(parseInt(<string>currentReports?.row_count));
						projectReports.push(parseInt(<string>currentProjectReports?.row_count));
						allPoliceReports.push(parseInt(<string>currentPoliceReports?.row_count));
						projectPoliceReports.push(parseInt(<string>currentProjectPoliceReports?.row_count));
					}

					if (allResponses.length > 0) {
						this.app == 'Jotto'
							? ((this.series[0] = { label: 'Total', data: allProjectResponses }), (this.series[1] = { label: 'Video', data: projectVideos }), (this.series[2] = { label: 'Text', data: projectTexts }), (this.series[3] = { label: 'Audio', data: projectAudios }))
							: this.app == 'Mobile Justice'
							? ((this.series[0].data = allResponses), (this.series[1].data = projectVideos), (this.series[2].data = projectReports))
							: ((this.series[0].data = allResponses), (this.series[1].data = projectVideos), (this.series[2].data = projectReports), (this.series[3].data = projectPoliceReports));

						this.allCount = <number>data.total_count;
						this.allProjectCount = <number>data.total_project_count;
						this.allTextCount = <number>data.total_texts_count;
						this.projectTextCount = <number>data.total_project_texts_count;
						this.allReportCount = <number>data.total_reports_count;
						this.projectReportCount = <number>data.total_project_reports_count;
						this.allPoliceReportCount = <number>data.total_policeReports_count;
						this.projectPoliceReportCount = <number>data.total_project_policeReports_count;
						this.allAudioCount = <number>data.total_audios_count;
						this.projectAudioCount = <number>data.total_project_audios_count;
						this.allVideoCount = <number>data.total_videos_count;
						this.projectVideoCount = <number>data.total_project_videos_count;
						this.dateInfo = moment().format('LL');

						const counts = {
							text: this.projectTextCount,
							audio: this.projectAudioCount,
							video: this.projectVideoCount,
							total: this.allProjectCount,
						};
						this.responsesCountEmit.emit(counts);
					}
				}),
				finalize(() => {
					this.labels = this.date;
					const dataset = this.series.map((i: any) => {
						return { label: i.label, data: i.data, tension: 0.4 };
					});
					const dates = this.labels.map((i: any) => format(new Date(i), 'MMM dd'));

					this.lineChartData = {
						datasets: dataset,
						labels: dates,
					};
					this.formLoading = false;
				})
			)
		);
	}

	changeTimeframe(event: any) {
		this.timeframe = event.target.value;
		this.getChart(this.timeframe, this.currentFilters);
	}
}
